import type { NavigateFunction } from "react-router-dom";
import type { CanvasNavigationConfig } from "../../types";

const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export interface CanvasNavigationContext {
  workspaceId: string;
  currentRowId?: string | null;
  selectedRowId?: string | null;
  returnPath: string;
  authFetch: (
    path: string,
    description: string,
    options?: object,
  ) => Promise<unknown>;
  navigate: NavigateFunction;
}

export async function openCanvasNavigation(
  config: CanvasNavigationConfig,
  context: CanvasNavigationContext,
): Promise<void> {
  const { workspaceId, authFetch, navigate } = context;
  const canvasId = config.target_canvas_id;
  const viewId = config.target_view_id;
  if (
    !workspaceId ||
    !!canvasId === !!viewId ||
    !UUID.test(canvasId || viewId || "")
  ) {
    throw new Error("Destination unavailable or access denied.");
  }

  const source = config.record_source || "none";
  const rowId =
    source === "current"
      ? context.currentRowId
      : source === "selected"
        ? context.selectedRowId
        : null;
  if (source !== "none" && !rowId) {
    throw new Error("Select a record before opening this destination.");
  }

  try {
    if (canvasId) {
      await authFetch(
        `/api/workspace/myvault/canvases/${canvasId}/full`,
        "check navigation destination",
        { headers: { "x-workspace-id": workspaceId } },
      );
    } else {
      await authFetch(
        `/api/workspace/myvault/views/${viewId}`,
        "check navigation destination",
        { headers: { "x-workspace-id": workspaceId } },
      );
    }
  } catch {
    throw new Error("Destination unavailable or access denied.");
  }

  const path = canvasId
    ? `/workspace/${encodeURIComponent(workspaceId)}/myvault/canvases/${canvasId}`
    : `/workspace/${encodeURIComponent(workspaceId)}/myvault/views/${viewId}`;
  const query = rowId ? `?row=${encodeURIComponent(rowId)}` : "";
  navigate(`${path}${query}`, {
    state:
      config.return_to_source === false
        ? undefined
        : { canvasReturnTo: context.returnPath },
  });
}
